import React, { useState, useCallback } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { reportIssue } from "../../utils/issueReportApi";

/**
 * Reusable one-click issue reporter.
 *
 * Drop into any screen where something might be broken (link, action, etc.).
 *
 * @param {object}   props
 * @param {string}   props.issueType   - e.g. "broken_link"
 * @param {string}   props.url         - the failing URL / resource
 * @param {string}   props.source      - screen or component name
 * @param {string}   [props.description]
 * @param {object}   [props.contextData]
 * @param {string}   [props.buttonLabel]  - defaults to "Submit report to CivixThread"
 * @param {string}   [props.warningText]  - shown above the button
 * @param {Function} [props.onSuccess]
 * @param {Function} [props.onError]
 */
export default function ReportIssueAction({
  issueType,
  url,
  source,
  description = "",
  contextData,
  buttonLabel = "Submit report to CivixThread",
  warningText = "This link may be unavailable.",
  onSuccess,
  onError,
}) {
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleReport = useCallback(async () => {
    if (status === "submitting" || status === "success") return;
    setStatus("submitting");
    try {
      await reportIssue({ issueType, url, source, description, contextData });
      setStatus("success");
      onSuccess?.();
    } catch {
      setStatus("error");
      onError?.();
    }
  }, [issueType, url, source, description, contextData, onSuccess, onError, status]);

  if (status === "success") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1,
          px: 1.5,
          borderRadius: "10px",
          backgroundColor: "#ecfdf5",
        }}
      >
        <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18, color: "#047857" }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#047857" }}>
          Thanks, report submitted.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: "10px",
        backgroundColor: "#fffbeb",
        border: "1px solid #fde68a",
        p: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
        <ReportProblemOutlinedIcon sx={{ fontSize: 18, color: "#b45309", mt: "1px" }} />
        <Typography sx={{ fontSize: 13, color: "#92400e", lineHeight: 1.5 }}>
          {warningText}
        </Typography>
      </Box>

      <Button
        size="small"
        variant="outlined"
        onClick={handleReport}
        disabled={status === "submitting"}
        sx={{
          textTransform: "none",
          fontSize: 13,
          fontWeight: 600,
          borderRadius: "8px",
          borderColor: "#d97706",
          color: "#92400e",
          px: 1.5,
          py: 0.5,
          minHeight: 34,
          "&:hover": { borderColor: "#b45309", backgroundColor: "#fef3c7" },
        }}
      >
        {status === "submitting" ? (
          <CircularProgress size={16} sx={{ color: "#b45309", mr: 1 }} />
        ) : null}
        {status === "error" ? "Try again" : buttonLabel}
      </Button>
    </Box>
  );
}
